import React, { useState } from 'react';
import { useQuote } from '../contexts/QuoteContext';

const QuoteSidebar: React.FC = () => {
  const { quoteState, calculateTotalPrice, updateCustomerDetails } = useQuote();
  const [buttonState, setButtonState] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string>('');

  const totalPrice = calculateTotalPrice();

  const isQuoteComplete = () => {
    return (
      quoteState.selectedProduct &&
      quoteState.selectedModel &&
      quoteState.selectedVariant &&
      quoteState.selectedColor &&
      quoteState.selectedDeliveryLocation &&
      quoteState.customerDetails.name &&
      quoteState.customerDetails.email &&
      quoteState.customerDetails.phone
    );
  };

  const areStepsCompleteButDetailsMissing = () => {
    const stepsComplete = (
      quoteState.selectedProduct &&
      quoteState.selectedModel &&
      quoteState.selectedVariant &&
      quoteState.selectedColor &&
      quoteState.selectedDeliveryLocation
    );

    const detailsMissing = !(
      quoteState.customerDetails.name &&
      quoteState.customerDetails.email &&
      quoteState.customerDetails.phone
    );

    return stepsComplete && detailsMissing;
  };

  const compileQuoteData = () => {
    return {
      productType: quoteState.selectedProduct,
      model: quoteState.selectedModel?.name,
      variant: quoteState.selectedVariant?.name,
      upgrades: quoteState.selectedUpgrades.map(upgrade => upgrade.name),
      color: quoteState.selectedColor?.name,
      deliveryLocation: quoteState.selectedDeliveryLocation?.name,
      customerEmail: quoteState.customerDetails.email,
      customerName: quoteState.customerDetails.name,
      customerPhone: quoteState.customerDetails.phone
    };
  };

  const handleSendQuote = async () => {
    console.log('[handleSendQuote] ➔ Clicked');

    if (!isQuoteComplete()) {
      console.log('[handleSendQuote] ➔ Quote is not complete');
      return;
    }

    setButtonState('sending');
    setErrorMessage('');

    try {
      const quoteData = compileQuoteData();

      console.log('[handleSendQuote] ➔ Sending quote emails...');

      const apiUrl = import.meta.env.VITE_API_URL || '';
      const response = await fetch(`${apiUrl}/api/quotes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(quoteData),
      });

      if (!response.ok) {
        console.log('[handleSendQuote] ➔ Failed to send quote');
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to send quote');
      }

      const result = await response.json();
      console.log('[handleSendQuote] ➔ Quote sent successfully:', result);

      setButtonState('success');
    } catch (error) {
      console.error('[handleSendQuote] ➔ Error sending quote:', error);
      setButtonState('error');
      setErrorMessage(error instanceof Error ? error.message : 'Failed to send quote. Please try again.');
    }
  };

  return (
    <div className="sidebar">
      <div className="sidebar-sticky-wrapper">
        <h2 className="sidebar-title">Quote Summary</h2>

        {/* Product Configuration */}
        <div className="summary-card">
          <h3 className="card-title">Product Configuration</h3>
          <div className="configuration-details">
            {quoteState.selectedProduct && (
              <div className="config-item-selected">
                ✓ Product Type: {quoteState.selectedProduct === 'camper' ? 'Camper' : 'Trailer'}
              </div>
            )}
            {!quoteState.selectedModel && quoteState.selectedProduct && (
              <div className="config-item-pending">
                Select Model to complete your quote
              </div>
            )}
            {quoteState.selectedModel && (
              <div className="config-item-selected">
                ✓ Model: {quoteState.selectedModel.name}
              </div>
            )}
            {!quoteState.selectedVariant && quoteState.selectedModel && (
              <div className="config-item-pending">
                Select Variant to complete your quote
              </div>
            )}
            {quoteState.selectedVariant && (
              <div className="config-item-selected">
                ✓ Variant: {quoteState.selectedVariant.name}
              </div>
            )}
            {quoteState.selectedUpgrades.length > 0 && (
              <div className="config-item-selected">
                ✓ Upgrades: {quoteState.selectedUpgrades.reduce((total, upgrade) => total + upgrade.quantity, 0)} items selected
              </div>
            )}
            {quoteState.selectedColor && (
              <div className="config-item-selected">
                ✓ Color: {quoteState.selectedColor.name}
              </div>
            )}
            {!quoteState.selectedColor && quoteState.selectedVariant && (
              <div className="config-item-pending">
                Select Color to complete your quote
              </div>
            )}
            {!quoteState.selectedDeliveryLocation && quoteState.selectedColor && (
              <div className="config-item-pending">
                Select Delivery Location to complete your quote
              </div>
            )}
            {quoteState.selectedDeliveryLocation && (
              <div className="config-item-selected">
                ✓ Delivery: {quoteState.selectedDeliveryLocation.name}
              </div>
            )}
            {!quoteState.selectedProduct && (
              <p className="card-description">Select your options to see your summary.</p>
            )}
          </div>
        </div>

        {/* Estimated Price */}
        <div className="summary-card">
          <h3 className="card-title">Estimated Price</h3>
          <div className="price">${totalPrice.toLocaleString()}</div>
          <p className="price-disclaimer">
            * Prices are estimates only.
            <br />Final costs may vary.</p>
        </div>

        {/* Customer Details */}
        <div className="summary-card">
          <h3 className="card-title">Customer Details</h3>
          <div className="form-fields">
            <input
              type="text"
              placeholder="Customer Name"
              className="form-input"
              value={quoteState.customerDetails.name}
              onChange={(e) => updateCustomerDetails('name', e.target.value)}
            />
            <input
              type="email"
              placeholder="Email Address"
              className="form-input"
              value={quoteState.customerDetails.email}
              onChange={(e) => updateCustomerDetails('email', e.target.value)}
            />
            <input
              type="tel"
              placeholder="Phone Number"
              className="form-input"
              value={quoteState.customerDetails.phone}
              onChange={(e) => updateCustomerDetails('phone', e.target.value)}
            />
          </div>
        </div>

        {areStepsCompleteButDetailsMissing() && (
          <div className="customer-details-message">
            To receive your quote via email, and have one of our team members contact you, please complete your details above.
          </div>
        )}

        {buttonState === 'error' && errorMessage && (
          <div className="customer-details-message">
            {errorMessage}
          </div>
        )}

        {/* Save Quote Button */}
        <button
          className={`save-quote-button ${buttonState === 'success' ? 'success' : ''}`}
          disabled={!isQuoteComplete() || buttonState === 'sending' || buttonState === 'success'}
          onClick={handleSendQuote}
        >
          {buttonState === 'sending' && (
            <>
              <div className="loading-spinner"></div>
              Sending...
            </>
          )}
          {buttonState === 'success' && 'Quote Sent!'}
          {buttonState === 'error' && 'Send Quote'}
          {buttonState === 'idle' && 'Send Quote'}
        </button>

        {/* Version Number */}
        <div className="version-info">
          {import.meta.env.VITE_VERSION || '0.0.0'}
        </div>
      </div>
    </div>
  );
};

export default QuoteSidebar;
